import React, { useEffect, useState } from "react";
import { Input, Upload } from "antd";
import { toast } from "react-toastify";
import ENDPOINTS from "../../../../services/endpoint";
import apiInstance from "../../../../services/apiService";
import GradientButton from "../../../common/UI/GradientButton";
import SettingsSidebarHeader from "../../../common/SettingsSidebarHeader";
import "./index.scss";
import { EditIcon } from "../../../icons";

interface EditProfileProps {
  handleBackToPrevious: () => void;
}
interface UserDetails {
  fullName: string;
  phoneNumber: string;
  profileImage: string;
}

const EditProfile: React.FC<EditProfileProps> = ({ handleBackToPrevious }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [userName, setUserName] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setImageUrl(e.target.result as string);
        setSelectedFile(file);
      }
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleSaveProfile = async () => {
    if (!userName) {
      toast.error("Please enter a name.");
      return;
    }

    const formData = new FormData();
    formData.append("fullName", userName);

    if (selectedFile) {
      formData.append("file", selectedFile);
    }

    const url = ENDPOINTS.PROFILE_SETTINGS;

    try {
      setLoading(true);
      const response = await apiInstance.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile updated successfully!");
      setImageUrl(response.data.data.profileImage);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getUserProfileSettings = async () => {
      const url = ENDPOINTS.PROFILE_SETTINGS;

      setLoading(true);

      try {
        const response = await apiInstance.get(url);
        const data = response.data.data;
        setUserDetails(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    getUserProfileSettings();
  }, []);

  useEffect(() => {
    if (userDetails) {
      setUserName(userDetails?.fullName);
      setUserPhone(userDetails.phoneNumber);
      setImageUrl(userDetails.profileImage);
    }
  }, [userDetails]);

  return (
    <div className="edit-profile-container">
      <SettingsSidebarHeader
        title="Edit Profile"
        handleBackClick={handleBackToPrevious}
      />

      <Upload
        className="circular-upload"
        multiple={false}
        showUploadList={false}
        accept="image/*"
        beforeUpload={(file) => {
          const isImage = file.type.startsWith("image/");

          if (!isImage) {
            toast.error("You can only upload image files!");
            return Upload.LIST_IGNORE;
          }

          return handleImageUpload(file);
        }}
      >
        <div className="upload-container">
          {imageUrl ? (
            <img src={imageUrl} alt="Profile" className="uploaded-image" />
          ) : (
            <span>
              Add <br /> photo
            </span>
          )}
        </div>
      </Upload>

      <div className="name-input-container">
        <label className="input-label">Name: </label>
        <Input
          value={userName}
          className="input"
          disabled={loading}
          placeholder={loading ? "Loading..." : ""}
          onChange={(e) => setUserName(e.target.value)}
        />
        <div className="edit-icon">
          <EditIcon />
        </div>
      </div>

      <h2 className="phone-label">Phone Number</h2>
      <div className="phone-input-container">
        <label className="input-label">eFone</label>
        <Input
          readOnly
          value={userPhone}
          disabled={loading}
          className="input"
          placeholder={loading ? "Loading..." : ""}
        />
      </div>

      <div className="save-btn-container">
        <GradientButton onClick={handleSaveProfile} loading={loading}>
          Save
        </GradientButton>
      </div>
    </div>
  );
};

export default EditProfile;
